import React from "react";
import ActionButtons from "./ActionButtons";
import styles from "./dataTable.module.css";

import { useAuth } from "../../../contexts/AuthContext";

import { formatDateIST, formatDateTimeIST } from "../../../utils/dateFormatter";

const isIdColumn = (col = {}) => {
  const key = String(col.key || "").trim().toLowerCase();
  const label = String(col.label || "").trim().toLowerCase();
  return (
    key === "id" ||
    key === "fid" ||
    key.endsWith("_id") ||
    key.endsWith("_fid") ||
    label === "id"
  );
};

const DataTable = ({
  columns,
  data,
  loading,
  modelName,
  route,
  onEdit,
  onView,
  onDelete,
  onStatusToggle,
  primaryKey = "id",
}) => {
  const { hasActionAccess } = useAuth();

  const canView = hasActionAccess(route, "view");
  const canEdit = hasActionAccess(route, "edit");
  const canDelete = hasActionAccess(route, "delete");
  const visibleColumns = (columns || []).filter((col) => col?.key && !isIdColumn(col));

  const showActionColumn = canView || canEdit || canDelete;
  const normalizedModelName = String(modelName || "").toLowerCase();
  const quotationStatusOptions = [
    { label: "Draft", value: "draft" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ];

  /**
   * LOADING
   */

  if (loading) {
    return <div className={styles.loading}>Loading Data...</div>;
  }

  /**
   * NO DATA
   */

  if (!data || data.length === 0) {
    return (
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={
                    col.align === "center"
                      ? styles.alignCenter
                      : col.align === "right"
                      ? styles.alignRight
                      : ""
                  }
                >
                  {col.label}
                </th>
              ))}

              {showActionColumn && <th>Action</th>}
            </tr>
          </thead>

          <tbody>
            <tr>
              <td
                colSpan={visibleColumns.length + (showActionColumn ? 1 : 0)}
                className={styles.noData}
              >
                No records found
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  /**
   * MAIN TABLE
   */

  return (
    <div className={styles.tableWrapper}>
      <table className={styles.table}>
        {/* HEADER */}

        <thead>
          <tr>
            {visibleColumns.map((col) => (
              <th
                key={col.key}
                className={
                  col.align === "center"
                    ? styles.alignCenter
                    : col.align === "right"
                    ? styles.alignRight
                    : ""
                }
              >
                {col.label}
              </th>
            ))}

            {showActionColumn && <th>Action</th>}
          </tr>
        </thead>

        {/* BODY */}

        <tbody>
          {data.map((row, index) => {
            const rowKey = row[primaryKey] || row.id || row.user_id || index;

            return (
              <tr
                key={rowKey}
                className={index % 2 === 0 ? styles.even : styles.odd}
              >
                {/* COLUMNS */}

                {visibleColumns.map((col) => {
                  const value = row[col.key];

                  /**
                   * ? DATETIME FORMAT
                   */

                  if (col.type === "datetime") {
                    return (
                      <td
                        key={col.key}
                        className={
                          col.align === "center"
                            ? styles.alignCenter
                            : col.align === "right"
                            ? styles.alignRight
                            : ""
                        }
                      >
                        {formatDateTimeIST(value)}
                      </td>
                    );
                  }
                  if (col.type === "date") {
                    return (
                      <td
                        key={col.key}
                        className={
                          col.align === "center"
                            ? styles.alignCenter
                            : col.align === "right"
                            ? styles.alignRight
                            : ""
                        }
                      >
                        {formatDateIST(value)}
                      </td>
                    );
                  }

                  /**
                   * ? NUMERIC FORMAT (2 decimal places)
                   */

                  if (col.type === "number") {
                    const numValue = parseFloat(value);
                    const formattedValue = isNaN(numValue) ? "-" : numValue.toFixed(2);
                    return (
                      <td
                        key={col.key}
                        className={
                          col.align === "center"
                            ? styles.alignCenter
                            : col.align === "right"
                            ? styles.alignRight
                            : ""
                        }
                      >
                        {formattedValue}
                      </td>
                    );
                  }

                  /**
                   * STATUS PILL (active/inactive)
                   */

                  if (
                    (col.key === "status" ||
                      String(col.key || "").toLowerCase().endsWith("_status") ||
                      String(col.label || "").toLowerCase() === "status") &&
                    typeof value === "string"
                  ) {
                    const normalizedStatus = value.trim().toLowerCase();
                    if (
                      normalizedModelName === "quotation" &&
                      col.key === "quotation_progress_status"
                    ) {
                      if (["po_partially", "po_completed"].includes(normalizedStatus)) {
                        return (
                          <td
                            key={col.key}
                            className={
                              col.align === "center"
                                ? styles.alignCenter
                                : col.align === "right"
                                ? styles.alignRight
                                : ""
                            }
                          >
                            <span className={`${styles.statusPill} ${styles.statusDefault}`}>
                              {value}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={col.key}
                          className={
                            col.align === "center"
                              ? styles.alignCenter
                              : col.align === "right"
                              ? styles.alignRight
                              : ""
                          }
                        >
                          <select
                            className={styles.statusSelect}
                            value={normalizedStatus}
                            onChange={(e) => onStatusToggle(row, e.target.value)}
                            disabled={!canEdit}
                          >
                            {quotationStatusOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      );
                    }

                    const statusClass =
                      normalizedStatus === "active"
                        ? styles.statusActive
                        : normalizedStatus === "inactive"
                        ? styles.statusInactive
                        : styles.statusDefault;

                    return (
                      <td
                        key={col.key}
                        className={
                          col.align === "center"
                            ? styles.alignCenter
                            : col.align === "right"
                            ? styles.alignRight
                            : ""
                        }
                      >
                        <span className={`${styles.statusPill} ${statusClass}`}>
                          {value}
                        </span>
                      </td>
                    );
                  }

                  /**
                   * BOOLEAN
                   */

                  if (typeof value === "boolean") {
                    return (
                      <td
                        key={col.key}
                        className={
                          col.align === "center"
                            ? styles.alignCenter
                            : col.align === "right"
                            ? styles.alignRight
                            : ""
                        }
                      >
                        {value ? "Yes" : "No"}
                      </td>
                    );
                  }

                  /**
                   * IMAGE
                   */

                  if (
                    value &&
                    typeof value === "string" &&
                    value.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                  ) {
                    const normalizedPath = value.replace(/\\/g, "/");

                    const isAbsolute = /^https?:\/\//i.test(normalizedPath);
                    const withUploads = normalizedPath.startsWith("uploads/")
                      ? normalizedPath
                      : normalizedPath.startsWith("/uploads/")
                      ? normalizedPath.slice(1)
                      : `uploads/${normalizedPath}`;

                    const imageUrl = isAbsolute
                      ? normalizedPath
                      : `${process.env.REACT_APP_SERVER_URL}/${withUploads}`;

                    return (
                      <td
                        key={col.key}
                        className={
                          col.align === "center"
                            ? styles.alignCenter
                            : col.align === "right"
                            ? styles.alignRight
                            : ""
                        }
                      >
                        <img
                          src={imageUrl}
                          alt="img"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                          }}
                        />
                      </td>
                    );
                  }

                  /**
                   * EMPTY
                   */

                  if (value === null || value === undefined || value === "") {
                    return (
                      <td
                        key={col.key}
                        className={
                          col.align === "center"
                            ? styles.alignCenter
                            : col.align === "right"
                            ? styles.alignRight
                            : ""
                        }
                      >
                        -
                      </td>
                    );
                  }

                  /**
                   * DEFAULT
                   */

                  return (
                    <td
                      key={col.key}
                      className={
                        col.align === "center"
                          ? styles.alignCenter
                          : col.align === "right"
                          ? styles.alignRight
                          : ""
                      }
                    >
                      {value}
                    </td>
                  );
                })}

                {/* ACTION */}

                {showActionColumn && (
                  <td>
                    <ActionButtons
                      row={row}
                      modelName={normalizedModelName}
                      route={route}
                      onEdit={onEdit}
                      onView={onView}
                      onDelete={onDelete}
                      onStatusToggle={onStatusToggle}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DataTable;
